import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { usePostsStore } from '../stores/postsStore';
import { useDialogStore } from '../stores/dialogStore';
import { useGalleriesStore } from '../stores/galleriesStore';
import { useLayoutsStore } from '../stores/layoutsStore';
import RichTextEditorWithMentions from './RichTextEditorWithMentions';
import ProfileAvatar from './ProfileAvatar';
import GalleryRenderer from './GalleryRenderer';
import ProtectedImage from './ProtectedImage';
import ProtectedVideo from './ProtectedVideo';

const CreatePost = ({ postOnProfileId = null }) => {
  const { accessToken, profile } = useAuthStore();
  const { createPost } = usePostsStore();
  const { alert } = useDialogStore();
  const {
    createGallery,
    deleteGallery,
    fetchGallery,
    addMedia,
    deleteMedia,
    updateGallery,
    reorderMedia
  } = useGalleriesStore();
  const { mediaLayouts, fetchMediaLayouts } = useLayoutsStore();

  const [isCreating, setIsCreating] = useState(false);
  const [postBody, setPostBody] = useState('');
  const [isHero, setIsHero] = useState(false);
  const [galleryId, setGalleryId] = useState(null);
  const [localGalleryMedia, setLocalGalleryMedia] = useState([]);
  const [galleryLayout, setGalleryLayout] = useState(null);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [viewingMedia, setViewingMedia] = useState(null);

  // Load media layouts
  useEffect(() => {
    if (accessToken) {
      fetchMediaLayouts(accessToken);
    }
  }, [accessToken, fetchMediaLayouts]);

  const handleCreate = async () => {
    if (!postBody.trim()) {
      alert('Error', 'Post cannot be empty');
      return;
    }

    const result = await createPost(accessToken, {
      body: postBody,
      is_hero: isHero ? 1 : 0,
      gallery_id: galleryId, // Link gallery to post
      post_on_profile_id: postOnProfileId // Which profile's timeline to post on
    }, postOnProfileId);

    if (result.success) {
      setPostBody('');
      setIsHero(false);
      setGalleryId(null);
      setLocalGalleryMedia([]);
      setGalleryLayout(null);
      setIsCreating(false);
    } else {
      alert('Error', result.message || 'Failed to create post');
    }
  };

  const handleCancel = async () => {
    // If gallery was created but post not submitted, delete the gallery
    if (galleryId) {
      await deleteGallery(galleryId, accessToken);
    }
    setPostBody('');
    setGalleryId(null);
    setLocalGalleryMedia([]);
    setGalleryLayout(null);
    setIsCreating(false);
  };

  // Gallery handlers
  const handleCreateGallery = async () => {
    if (!profile?.id) {
      alert('Error', 'Profile not found');
      return;
    }

    const result = await createGallery(
      {
        title: 'New Post Gallery',
        description: '',
        visibility_id: 2, // MEMBERS default
      },
      accessToken
    );

    if (result.success) {
      setGalleryId(result.gallery.id);
      setLocalGalleryMedia([]);
      setGalleryLayout(null);
    } else {
      alert('Error', result.error || 'Failed to create gallery');
    }
  };

  const handleGalleryLayoutChange = async (layoutCode) => {
    if (!galleryId) return;

    // Optimistically update UI
    setGalleryLayout(layoutCode);

    const result = await updateGallery(galleryId, { layout_code: layoutCode }, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to update gallery layout');
      // Revert on error
      const galleryResult = await fetchGallery(galleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery) {
        setGalleryLayout(galleryResult.gallery.layout_code);
      }
    }
  };

  const handleAddMediaToGallery = async (files) => {
    if (!galleryId || !files || files.length === 0) return;

    setUploadingGallery(true);

    const result = await addMedia(galleryId, files, accessToken);

    if (!result.success) {
      await alert('Error', result.error || 'Failed to upload media');
    } else {
      // Refresh gallery to show new media
      const galleryResult = await fetchGallery(galleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setLocalGalleryMedia(galleryResult.gallery.media);
      }
    }

    setUploadingGallery(false);
  };

  const handleDeleteMediaFromGallery = async (mediaId) => {
    if (!galleryId) return;

    const result = await deleteMedia(galleryId, mediaId, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to delete media');
    } else {
      // Refresh gallery
      const galleryResult = await fetchGallery(galleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setLocalGalleryMedia(galleryResult.gallery.media);
      }
    }
  };

  const handleDeleteGallery = async () => {
    if (!galleryId) return;

    const result = await deleteGallery(galleryId, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to delete gallery');
    } else {
      setGalleryId(null);
      setLocalGalleryMedia([]);
      setGalleryLayout(null);
    }
  };

  const handleReorderMedia = async (mediaOrder) => {
    if (!galleryId) return;

    const result = await reorderMedia(galleryId, mediaOrder, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to reorder media');
      // Refresh gallery to revert optimistic update
      const galleryResult = await fetchGallery(galleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setLocalGalleryMedia(galleryResult.gallery.media);
      }
    }
  };

  const handleViewMedia = (media) => {
    setViewingMedia(media);
  };

  if (!isCreating) {
    return (
      <div className="surface rounded-lg shadow-lg p-6 mb-6">
        <button
          onClick={() => setIsCreating(true)}
          className="w-full text-left px-4 py-3 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-3"
        >
          <ProfileAvatar
            profileId={profile?.id}
            displayName={profile?.display_name}
            size="md"
          />
          <span className="text-secondary">What's on your mind, {profile?.display_name}?</span>
        </button>
      </div>
    );
  }

  return (
    <div className="surface rounded-lg shadow-lg p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <ProfileAvatar
          profileId={profile?.id}
          displayName={profile?.display_name}
          size="md"
        />
        <div>
          <h3 className="font-semibold text-primary">{profile?.display_name}</h3>
          <p className="text-xs text-secondary">Creating a post</p>
        </div>
      </div>

      <RichTextEditorWithMentions
        value={postBody}
        onChange={setPostBody}
        postId={0}
        placeholder="What's on your mind?"
        isHero={isHero}
        onHeroChange={setIsHero}
      />

      {/* Gallery Section */}
      {galleryId ? (
        <div className="mt-4">
          <GalleryRenderer
            media={localGalleryMedia}
            layout={galleryLayout}
            editMode={true}
            onLayoutChange={handleGalleryLayoutChange}
            onAddMedia={handleAddMediaToGallery}
            onDeleteMedia={handleDeleteMediaFromGallery}
            onDeleteGallery={handleDeleteGallery}
            onViewMedia={handleViewMedia}
            onReorderMedia={handleReorderMedia}
            availableLayouts={mediaLayouts.filter(l => l.is_active && l.type_id === 2)}
            uploading={uploadingGallery}
          />
        </div>
      ) : (
        <div className="mt-4">
          <button
            onClick={handleCreateGallery}
            className="w-full px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded text-secondary hover:border-primary-600 hover:text-primary-600 text-sm"
          >
            + Add Media Gallery
          </button>
        </div>
      )}

      <div className="flex gap-2 mt-4">
        <button
          onClick={handleCreate}
          className="px-6 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
        >
          Post
        </button>
        <button
          onClick={handleCancel}
          className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
      </div>

      {/* View Media Modal */}
      {viewingMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]"
          onClick={() => setViewingMedia(null)}
        >
          <button
            onClick={() => setViewingMedia(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="max-w-6xl max-h-[90vh] p-4" onClick={(e) => e.stopPropagation()}>
            {viewingMedia.kind_code === 'IMAGE' ? (
              <ProtectedImage
                uuid={viewingMedia.uuid}
                alt={viewingMedia.storage_key}
                className="max-w-full max-h-[90vh] object-contain"
              />
            ) : (
              <ProtectedVideo
                uuid={viewingMedia.uuid}
                controls={true}
                autoPlay={true}
                className="max-w-full max-h-[90vh]"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatePost;
